/**
 * Single source of truth for backend configuration on the frontend.
 *
 * `amplify_outputs.json` is the file `npx ampx pipeline-deploy` writes after
 * the backend stack finishes synthesizing. It contains all backend resource
 * outputs — Cognito user-pool IDs, the API Gateway URL, S3 bucket names, etc.
 *
 * In production:
 *   amplify.yml copies it from the project root into `frontend/` after the
 *   backend phase, before the frontend webpack build runs. The frontend bundle
 *   therefore knows the API URL on its very first build — no second deploy.
 *
 * Locally:
 *   The file ships as an empty `{}` placeholder; `IS_CONFIGURED` is false and
 *   the app boots in mock-mode using `services/mock-api.ts`.
 */

import outputs from '../amplify_outputs.json'

type AmplifyOutputs = {
  auth?: {
    user_pool_id?: string
    user_pool_client_id?: string
    identity_pool_id?: string
    aws_region?: string
    [k: string]: unknown
  }
  // Mixed shape: backend.addOutput() puts arbitrary nested objects here,
  // including both `{ API: { endpoint } }` and a flat `ApiUrl` string.
  custom?: Record<string, unknown>
  [k: string]: unknown
}

const o = outputs as unknown as AmplifyOutputs

export const amplifyOutputs = o
export const IS_CONFIGURED = !!o.auth?.user_pool_id

/** Find the API endpoint from the backend.addOutput() custom block. */
function findApiUrl(): string {
  const c = o.custom
  if (!c) return ''
  // Preferred: `custom.API.endpoint` (what backend.addOutput() writes)
  const api = c['API'] as { endpoint?: string } | undefined
  if (api?.endpoint) return api.endpoint
  // Legacy: a top-level ApiUrl string under custom
  if (typeof c['ApiUrl'] === 'string') return c['ApiUrl'] as string
  // Fallback: scan nested objects for any `ApiUrl` key (raw CDK CfnOutput shape)
  for (const v of Object.values(c)) {
    if (v && typeof v === 'object' && 'ApiUrl' in v) {
      const url = (v as Record<string, unknown>)['ApiUrl']
      if (typeof url === 'string') return url
    }
  }
  return ''
}

export const API_URL = findApiUrl()

/** Function URL for the auth-telemetry Lambda. Same flat-vs-nested
 * lookup pattern as findApiUrl — backend.ts emits both shapes. */
function findAuthTelemetryUrl(): string {
  const c = o.custom
  if (!c) return ''
  if (typeof c['AuthTelemetryUrl'] === 'string') return c['AuthTelemetryUrl'] as string
  for (const v of Object.values(c)) {
    if (v && typeof v === 'object' && 'AuthTelemetryUrl' in v) {
      const url = (v as Record<string, unknown>)['AuthTelemetryUrl']
      if (typeof url === 'string') return url
    }
  }
  return ''
}

export const AUTH_TELEMETRY_URL = findAuthTelemetryUrl()
